import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Button } from "@/shared/components/Button";
import { ADMIN_ROLES } from "@/lib/auth/admin-roles";
import { detectSellerMobilePlatform } from "@/lib/navigation/seller-app-launch";
import { buildRequestUrl } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import { useImpersonateStore } from "@/shared/services/useImpersonateStore";
import { useSessionQuery } from "@/shared/services/useSessionQuery";

const ALLOWED_IMPERSONATION_ROLES = [
  ADMIN_ROLES.ADMIN,
  ADMIN_ROLES.SUPER_ADMIN,
  ADMIN_ROLES.CUSTOMER_SERVICE,
] as const;

function hasImpersonationAccess(roles?: readonly string[] | null) {
  if (!roles?.length) {
    return false;
  }
  return roles.some((role) =>
    ALLOWED_IMPERSONATION_ROLES.includes(
      role as (typeof ALLOWED_IMPERSONATION_ROLES)[number],
    ),
  );
}

type Props = {
  storeId: string;
  locale: string;
  adminRoles?: string[];
  className?: string;
  variant?: "secondary" | "ghost";
  hideError?: boolean;
  onActionStart?: () => void;
  portal?: "seller" | "restaurant";
};

export function ImpersonateButton({
  storeId,
  locale,
  adminRoles,
  className,
  variant = "secondary",
  hideError = false,
  onActionStart,
  portal = "seller",
}: Props) {
  const t = useTranslations();
  const { data: session, isLoading: isSessionLoading } = useSessionQuery();
  const { impersonate, isSubmitting, error } = useImpersonateStore(
    storeId,
    locale,
    portal,
  );
  // The mobile path posts a real form instead of calling the API, so it has a
  // submit state of its own — the hook's belongs to the fetch it does not make.
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const mobileSubmitLockRef = useRef(false);
  const mobileSubmitResetRef = useRef<number | null>(null);
  const resolvedRoles = adminRoles ?? session?.admin?.roles ?? null;
  const mobilePlatform = detectSellerMobilePlatform({
    userAgent: window.navigator.userAgent,
    maxTouchPoints: window.navigator.maxTouchPoints,
  });

  const canImpersonate = useMemo(
    () => hasImpersonationAccess(resolvedRoles),
    [resolvedRoles],
  );

  useEffect(
    () => () => {
      if (mobileSubmitResetRef.current !== null) {
        window.clearTimeout(mobileSubmitResetRef.current);
      }
    },
    [],
  );

  if ((adminRoles === undefined && isSessionLoading) || !canImpersonate) {
    return null;
  }

  if (mobilePlatform && portal === "seller") {
    const launchPath = `${endpoints.admin.storeImpersonationAppLaunch(
      storeId,
    )}?${new URLSearchParams({ locale, platform: mobilePlatform }).toString()}`;

    return (
      <form
        method="POST"
        action={buildRequestUrl(launchPath)}
        target="_blank"
        className="flex flex-col gap-2"
        onSubmit={(event) => {
          if (mobileSubmitLockRef.current) {
            event.preventDefault();
            return;
          }
          mobileSubmitLockRef.current = true;
          setIsFormSubmitting(true);
          mobileSubmitResetRef.current = window.setTimeout(() => {
            mobileSubmitLockRef.current = false;
            setIsFormSubmitting(false);
          }, 2_000);
          // Let the browser perform the user-initiated form navigation before
          // a containing action menu unmounts this form.
          window.setTimeout(() => onActionStart?.(), 0);
        }}
      >
        <Button
          type="submit"
          variant={variant}
          className={["max-lg:min-h-10", className].filter(Boolean).join(" ")}
          disabled={isFormSubmitting}
        >
          {isFormSubmitting
            ? t(keys.admin.impersonateSellerPending)
            : t(keys.admin.impersonateSellerAction)}
        </Button>
      </form>
    );
  }

  const handleClick = async () => {
    onActionStart?.();
    await impersonate();
  };

  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant={variant}
        className={["max-lg:min-h-10", className].filter(Boolean).join(" ")}
        disabled={isSubmitting}
        onClick={handleClick}
      >
        {isSubmitting
          ? t(keys.admin.impersonateSellerPending)
          : t(keys.admin.impersonateSellerAction)}
      </Button>
      {!hideError && error ? (
        <p className="text-secondary text-danger-hover" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
